/**
 * ChordPro content utilities
 */

// Directives considered "section labels" — shown as plain-text headers in lyrics view
const SECTION_LABEL_RE = /^\{(c|comment|verse|v|chorus|bridge|pre[-_]chorus|tag|intro|outro|ending|interlude|solo)\s*:\s*(.*?)\s*\}$/i;

/** Chromatic scales for key transposition */
const SHARP_SCALE = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT_SCALE  = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
const FLAT_TO_IDX = { Db: 1, Eb: 3, Gb: 6, Ab: 8, Bb: 10, Cb: 11, Fb: 4, 'E#': 5, 'B#': 0 };

/** Keys whose root is a flat note — these default to flat accidental display */
const FLAT_ROOT_KEYS = new Set(['Db', 'Eb', 'Gb', 'Ab', 'Bb']);

/**
 * The 5 practical sharp↔flat pairs PLUS the two enharmonic naturals (Fb=E, Cb=B).
 * Natural notes (E, B, C, F, G, D, A) are not sharpened — only flats are converted to their
 * enharmonic equivalents when switching to # mode.
 */
const SHARP_TO_FLAT = { 'C#': 'Db', 'D#': 'Eb', 'F#': 'Gb', 'G#': 'Ab', 'A#': 'Bb' };
const FLAT_TO_SHARP = { 'Db': 'C#', 'Eb': 'D#', 'Fb': 'E', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#', 'Cb': 'B' };

/**
 * Notes that are always wrong regardless of accidental preference.
 * E# = F and B# = C in every musical context; Fb = E and Cb = B likewise.
 * Normalise these before any accidental conversion so they never appear in output.
 */
const ALWAYS_NORMALIZE = { 'E#': 'F', 'B#': 'C', 'Fb': 'E', 'Cb': 'B' };

/** Convert a single root note to the requested accidental, or leave it unchanged. */
function convertRoot(root, accidental) {
  // Always normalise theoretically-invalid enharmonics first (E#→F, B#→C, Fb→E, Cb→B)
  const normalized = ALWAYS_NORMALIZE[root] ?? root;
  if (accidental === 'b') return SHARP_TO_FLAT[normalized] ?? normalized;
  if (accidental === '#') return FLAT_TO_SHARP[normalized] ?? normalized;
  return normalized;
}

/**
 * Apply sharp/flat accidental to a chord text string (e.g. "F#m7", "D/F#", "Bbsus2").
 * Only converts the 5 practical sharp↔flat equivalents; natural notes are unchanged.
 */
function applyAccidentalToChordText(text, accidental) {
  if (!text || !accidental) return text;
  // Split off bass note: "D/F#" → root part "D", bass "F#"
  const slashIdx = text.indexOf('/');
  const rootPart = slashIdx === -1 ? text : text.slice(0, slashIdx);
  const bassPart = slashIdx === -1 ? null : text.slice(slashIdx + 1);

  // Parse root note (e.g. "F#", "Bb") + quality (e.g. "m7", "sus2")
  const rootMatch = rootPart.match(/^([A-G][b#]?)(.*)/);
  if (!rootMatch) return text;
  const [, root, quality] = rootMatch;
  const newRoot = convertRoot(root, accidental);

  let result = newRoot + quality;
  if (bassPart !== null) {
    const bassMatch = bassPart.match(/^([A-G][b#]?)(.*)/);
    if (bassMatch) {
      const [, bassRoot, bassQuality] = bassMatch;
      result += '/' + convertRoot(bassRoot, accidental) + bassQuality;
    } else {
      result += '/' + bassPart;
    }
  }
  return result;
}

/**
 * Detect whether a given key should default to sharp (#) or flat (b) notation.
 * Returns '#' or 'b'.
 */
export function detectAccidental(key) {
  if (!key) return '#';
  const m = key.match(/^([A-G][b#]?)/);
  if (!m) return '#';
  return FLAT_ROOT_KEYS.has(m[1]) ? 'b' : '#';
}

/**
 * Transpose a key string (e.g. "G", "Bb", "F#m") by the given semitone delta.
 * Preserves key suffix (e.g. "m" for minor).
 * accidental: '#' | 'b' | null  — forced output accidental; null = infer from original root.
 * Returns the original string unchanged if it cannot be parsed.
 */
export function transposeKey(key, semitones, accidental = null) {
  if (!key) return '';
  const m = key.match(/^([A-G][b#]?)(.*)/);
  if (!m) return key;
  const [, root, suffix] = m;
  const idx = SHARP_SCALE.indexOf(root) !== -1 ? SHARP_SCALE.indexOf(root) : FLAT_TO_IDX[root];
  if (idx === undefined || idx === -1) return key;
  if (!semitones) {
    // No transposition — just normalise the root to the requested accidental
    const newIdx = idx;
    const useFlat = accidental === 'b' || (accidental === null && FLAT_ROOT_KEYS.has(root));
    return (useFlat ? FLAT_SCALE : SHARP_SCALE)[newIdx] + suffix;
  }
  const newIdx = ((idx + semitones) % 12 + 12) % 12;
  const useFlat = accidental === 'b' || (accidental === null && FLAT_ROOT_KEYS.has(root));
  return (useFlat ? FLAT_SCALE : SHARP_SCALE)[newIdx] + suffix;
}

/**
 * Post-process ChordSheetJS HTML.
 * - Adds row--lyric-only class (collapse chord space on chord-free rows)
 * - Adds row--chord-only class (collapse lyric space on lyric-free rows)
 * - Optionally applies sharp/flat accidental conversion chord-by-chord
 *   (bypasses the ChordSheetJS Song.useAccidental bass-note bug)
 *
 * @param {string} html     - HTML string from HtmlDivFormatter
 * @param {string|null} accidental - '#' | 'b' | null (null = no conversion)
 */
export const postProcessChordHtml = (html, accidental = null) => {
  if (!html || typeof window === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  doc.querySelectorAll('.row').forEach(row => {
    const chords = row.querySelectorAll('.chord');
    const lyrics = row.querySelectorAll('.lyrics');
    // Lyric-only row: all chord divs are empty → collapse chord space
    if (chords.length && [...chords].every(c => !c.textContent.trim())) {
      row.classList.add('row--lyric-only');
    }
    // Chord-only row: all lyrics divs are empty → collapse lyrics space
    if (lyrics.length && [...lyrics].every(l => !l.textContent.trim())) {
      row.classList.add('row--chord-only');
    }
  });
  // Apply accidental conversion chord-by-chord using practical sharp↔flat pairs only.
  // (Natural notes like E, B, C, F, G are never converted to Fb, Cb, E#, B#, etc.)
  if (accidental) {
    doc.querySelectorAll('.chord').forEach(el => {
      const text = el.textContent.trim();
      if (text) el.textContent = applyAccidentalToChordText(text, accidental);
    });
  }
  return doc.body.firstChild.innerHTML;
};

/**
 * Strip ChordPro formatting to lyrics only.
 * Removes all [chord] brackets and meta directives.
 * Section labels like {c: Verse 1} are kept as "— Verse 1 —" markers.
 */
export const stripToLyrics = (content) => {
  if (!content) return '';
  const result = [];

  for (const rawLine of content.split('\n')) {
    const line = rawLine.trim();

    if (line.startsWith('{')) {
      const sectionMatch = line.match(SECTION_LABEL_RE);
      if (sectionMatch && sectionMatch[2]) {
        result.push(`\u2014 ${sectionMatch[2]} \u2014`);
      }
      // All other directives (meta info like title, artist, key, bpm, etc.) → skip
      continue;
    }

    // Strip chord brackets [Em], [C/G], [A#m7b5]
    const lyric = line.replace(/\[[^\]]*\]/g, '').trim();
    result.push(lyric);
  }

  // Collapse multiple consecutive blank lines into one, trim leading/trailing blanks
  return result
    .reduce((acc, l) => {
      if (l === '' && acc.length > 0 && acc[acc.length - 1] === '') return acc;
      acc.push(l);
      return acc;
    }, [])
    .join('\n')
    .trim();
};



